import TitrePage from "../components/charte/TitrePage";
import * as React from 'react';
import { noterActivite, EXPORTS } from '../utilities/journalActivite';
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, InputAdornment,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  Tabs, TextField, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import * as Papa from 'papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { loaderOff, loaderOn, logout, openSnackbar } from '../redux/actions';
import { useNavigate } from 'react-router-dom';
import store from '../redux/store';
import { getConfigActive, getUser } from '../utilities/adminservice';
import { deciderRetrait, getFileRetraits, getUserdetails, marquerRetraitVerse, messageServeur, verserRetraitWave, waveDisponible } from '../utilities/userService';
import { AIDE_ARGENT } from '../aide/textesAide';

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:           'var(--c-bg)',
  card:         'var(--c-surface)',
  border:       'var(--c-border)',
  borderAccent: '#1F1E1B',
  accent:       '#1F1E1B',
  accentLight:  '#57534A',
  accentMuted:  'rgba(31,30,27,0.07)',
  text:         '#1F1E1B',
  textSub:      '#57534A',
  textMuted:    '#57534A',
  success:      '#356A0F',
  warning:      '#7C5600',
  info:         '#1D5FA4',
  error:        '#A23A0D',
} as const;

const AVATAR_COLORS = ['#1F1E1B', '#356A0F', '#7C5600'];

function emailHash(email: string): number {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function CollectorAvatar({ email }: { email: string }) {
  const color = AVATAR_COLORS[emailHash(email) % 3];
  return (
    <Box sx={{
      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
      background: color + '20', border: `1.5px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color, lineHeight: 1 }}>
        {email.slice(0, 2).toUpperCase()}
      </Typography>
    </Box>
  );
}

function maskEmail(email: string): string {
  const parts = email.split('@');
  return parts[0].slice(0, 3) + '***@' + (parts[1] || '');
}


type WithdrawFilter = 'all' | 'ready' | 'pending';

/** Compte tel que renvoyé par user-service : solde et « Prêt au retrait » calculés par le serveur. */
interface Compte {
  user_id: number;
  email: string;
  token_avaliable: number;
  token_allocated: number;
  solde_cfa: number | null;
  pret_au_retrait: boolean | null;
}

interface Reglages {
  tipValueCfa: number;
  retraitMinimumCfa: number;
  retraitPlafondMensuelCfa: number;
  retraitSeuilDoubleApprobationCfa: number;
  retraitDoubleApprobationActive: boolean;
}

const LIBELLE_STATUT: Record<string, string> = {
  pending: 'En attente',
  premiere_approbation: 'Second avis requis',
  approved: 'Approuvé · à verser',
};

type Action = { type: 'approuver' | 'rejeter' | 'verser' | 'wave'; ligne: any } | null;

/* ongletInitial : 0 = soldes des Spotters (Argent › Soldes), 1 = file des
   retraits (Argent › Retraits). */
export default function TipsRetraits({ ongletInitial = 0 }: { ongletInitial?: number } = {}) {
  const [activeTab, setActiveTab] = useState(ongletInitial);
  useEffect(() => { setActiveTab(ongletInitial); }, [ongletInitial]);

  // ── Onglet 0 : soldes des Spotters (serveur) ─────────────────────────
  const [comptes, setComptes]         = useState<Compte[]>([]);
  const [reglages, setReglages]       = useState<Reglages | null>(null);
  const [page, setPage]               = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter]           = useState<WithdrawFilter>('all');

  // ── Onglet 1 : file des retraits (serveur) ───────────────────────────
  const [file, setFile]                 = React.useState<any[]>([]);
  const [filterText, setFilterText]     = React.useState('');
  const [payment_write, setPaymentWrite] = useState(false);
  const [action, setAction]             = useState<Action>(null);
  const [saisie, setSaisie]             = useState('');
  const [enCours, setEnCours]           = useState(false);
  // Clé Wave configurée côté serveur : bouton « Verser via Wave » affiché.
  const [waveOk, setWaveOk]             = useState(false);

  const navigate       = useNavigate();
  const dispatch       = useDispatch();
  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  const getRoleData = async () => {
    try {
      const res: any = await getUser();
      if (res?.role_type?.payment_write !== undefined) setPaymentWrite(res.role_type.payment_write);
    } catch (_) {
      dispatch(openSnackbar("Droits de l'administrateur illisibles : actions de modification masquées", "red"));
    }
  };

  const chargerComptes = async () => {
    dispatch(loaderOn());
    try {
      const data: any = await getUserdetails();
      setComptes(Array.isArray(data) ? data : []);
    } catch { /* silencieux */ }
    dispatch(loaderOff());
  };

  const chargerFile = async () => {
    try {
      setFile(await getFileRetraits());
    } catch {
      dispatch(openSnackbar('File des retraits indisponible', 'red'));
    }
  };

  useEffect(() => {
    chargerComptes();
    chargerFile();
    getRoleData();
    waveDisponible().then(setWaveOk);
    getConfigActive().then((c: any) => {
      if (c && typeof c.tipValueCfa === 'number') setReglages(c);
    });
  }, []);

  // ── Onglet 0 : état dérivé ───────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let r = comptes;
    if (filter === 'ready')   r = r.filter(c => c.pret_au_retrait === true);
    if (filter === 'pending') r = r.filter(c => c.pret_au_retrait === false);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      r = r.filter(c => (c.email || '').toLowerCase().includes(q));
    }
    return [...r].sort((a, b) => (b.token_avaliable || 0) - (a.token_avaliable || 0));
  }, [comptes, filter, searchQuery]);

  const slicedRows = filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const readyCount = comptes.filter(c => c.pret_au_retrait === true).length;
  const totalTips  = comptes.reduce((acc, c) => acc + (Number(c.token_avaliable) || 0), 0);
  const minimum    = reglages?.retraitMinimumCfa ?? null;

  const FILTER_LABELS: { key: WithdrawFilter; label: string }[] = [
    { key: 'all',     label: 'Tous' },
    { key: 'ready',   label: 'Prêts au retrait' },
    { key: 'pending', label: 'Sous le minimum' },
  ];

  // ── Onglet 1 : état dérivé ───────────────────────────────────────────
  const fileRows = React.useMemo(() => file.map(v => ({
    id:               v.id,
    collecteur:       ((v.user?.first_name || '') + ' ' + (v.user?.last_name || '')).trim() || v.user?.email || '',
    email:            v.user?.email || '',
    phone_number:     v.user?.phone_number || '',
    token_used:       v.token_used,
    amount_converted: v.amount_converted,
    status:           v.status,
    used_on:          v.used_on ? new Date(v.used_on).toLocaleString('fr-FR') : '',
    trace:            [
      v.premierApprobateur ? `1er avis : ${v.premierApprobateur}` : '',
      v.approuvePar ? `Approuvé : ${v.approuvePar}` : '',
    ].filter(Boolean).join(' · '),
  })), [file]);

  const filteredFileRows = useMemo(() => {
    const lower = filterText.toLowerCase();
    return lower ? fileRows.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(lower))) : fileRows;
  }, [fileRows, filterText]);

  const enAttente   = file.filter(v => v.status === 'pending' || v.status === 'premiere_approbation');
  const aVerser     = file.filter(v => v.status === 'approved');
  const montantFile = enAttente.reduce((s, v) => s + (Number(v.amount_converted) || 0), 0);
  const montantAVerser = aVerser.reduce((s, v) => s + (Number(v.amount_converted) || 0), 0);

  const executer = async () => {
    if (!action) return;
    const { type, ligne } = action;
    setEnCours(true);
    try {
      if (type === 'approuver') {
        const r: any = await deciderRetrait(ligne.id, 'approved');
        dispatch(openSnackbar(r?.status === 'premiere_approbation'
          ? 'Premier avis enregistré : un autre admin doit confirmer'
          : 'Retrait approuvé : à verser sur Wave', 'green'));
      } else if (type === 'rejeter') {
        await deciderRetrait(ligne.id, 'rejected', saisie.trim());
        dispatch(openSnackbar('Retrait rejeté : Tips rendus au Spotter', 'green'));
      } else if (type === 'wave') {
        const r: any = await verserRetraitWave(ligne.id);
        dispatch(openSnackbar(r?.referenceWave
          ? `Retrait versé via Wave (référence ${r.referenceWave})`
          : 'Retrait versé via Wave', 'green'));
      } else {
        await marquerRetraitVerse(ligne.id, saisie.trim());
        dispatch(openSnackbar('Retrait marqué versé', 'green'));
      }
      setAction(null);
      setSaisie('');
      await Promise.all([chargerFile(), chargerComptes()]);
    } catch (e: any) {
      dispatch(openSnackbar(messageServeur(e, 'Action refusée par le serveur'), 'red'));
    }
    setEnCours(false);
  };

  const handleExportMensuel = () => {
    const data = file.map(v => ({
      Date_demande:        v.used_on,
      Spotter:             ((v.user?.first_name || '') + ' ' + (v.user?.last_name || '')).trim(),
      Email:               v.user?.email || '',
      Téléphone:           v.user?.phone_number || '',
      Tips:                v.token_used,
      Montant_CFA:         v.amount_converted,
      Statut:              v.status,
      Premier_avis:        v.premierApprobateur || '',
      Premier_avis_le:     v.premiereApprobationLe || '',
      Approuve_par:        v.approuvePar || '',
      Approuve_le:         v.approuveLe || '',
    }));
    const csv =
      `# Tribe — file des retraits | Export : ${new Date().toISOString()} | Lignes : ${data.length}\n` +
      Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `retraits_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    noterActivite('export', EXPORTS.retraitsCsv);
  };

  const columns: GridColDef[] = [
    { field: 'collecteur', headerName: 'Spotter', width: 160, flex: 1 },
    { field: 'token_used', headerName: 'Tips', width: 90 },
    { field: 'amount_converted', headerName: 'Montant F CFA', width: 130,
      valueFormatter: (p: any) => p.value == null ? '' : Number(p.value).toLocaleString('fr-FR') },
    { field: 'phone_number', headerName: 'Téléphone', width: 130 },
    { field: 'used_on', headerName: 'Demandé le', width: 160 },
    {
      field: 'status', headerName: 'Statut', width: 170,
      renderCell: (params) => {
        const s = params.value as string;
        const color = s === 'approved' ? C.success : s === 'premiere_approbation' ? C.info : C.warning;
        return (
          <Chip label={LIBELLE_STATUT[s] || s} size="small" sx={{
            background: `${color}1a`, color, border: `1px solid ${color}40`,
            fontSize: 11, fontWeight: 600, height: 22,
          }} />
        );
      },
    },
    { field: 'trace', headerName: 'Décisions', width: 220 },
    {
      field: 'actions', headerName: 'Actions', width: 320,
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (params) => {
        const s = params.row.status;
        if (s === 'approved') {
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
              {waveOk && (
                <Button variant="contained" size="small" disabled={!payment_write}
                  sx={{ textTransform: 'none', background: C.success, fontSize: 12, px: 1.5, '&:hover': { background: '#28500B' } }}
                  onClick={() => setAction({ type: 'wave', ligne: params.row })}>
                  Verser via Wave
                </Button>
              )}
              <Button variant={waveOk ? 'outlined' : 'contained'} size="small" disabled={!payment_write}
                sx={waveOk
                  ? { textTransform: 'none', color: C.accentLight, borderColor: C.borderAccent, fontSize: 12, px: 1.5 }
                  : { textTransform: 'none', background: C.accent, fontSize: 12, px: 1.5, '&:hover': { background: C.accentLight } }}
                onClick={() => { setSaisie(''); setAction({ type: 'verser', ligne: params.row }); }}>
                Marquer versé
              </Button>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            <Button variant="contained" size="small" disabled={!payment_write}
              sx={{ textTransform: 'none', background: C.success, fontSize: 12, px: 1.5, '&:hover': { background: '#28500B' } }}
              onClick={() => setAction({ type: 'approuver', ligne: params.row })}>
              {s === 'premiere_approbation' ? '✓ Second avis' : '✓ Approuver'}
            </Button>
            <Button variant="outlined" size="small" disabled={!payment_write}
              sx={{ textTransform: 'none', color: C.error, borderColor: `${C.error}60`, fontSize: 12, px: 1.5, '&:hover': { borderColor: C.error, background: `${C.error}10` } }}
              onClick={() => { setSaisie(''); setAction({ type: 'rejeter', ligne: params.row }); }}>
              ✗ Rejeter
            </Button>
          </Box>
        );
      },
    },
  ];

  const montantAction = action ? Number(action.ligne.amount_converted || 0).toLocaleString('fr-FR') : '';
  const saisieRequise = action?.type === 'rejeter' || action?.type === 'verser';

  // ── Rendu ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: C.bg }}>
      <Dialog open={action !== null} onClose={() => !enCours && setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {action?.type === 'approuver' && 'Approuver le retrait'}
          {action?.type === 'rejeter' && 'Rejeter le retrait'}
          {action?.type === 'verser' && 'Marquer le retrait comme versé'}
          {action?.type === 'wave' && 'Verser le retrait via Wave'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {action?.ligne.collecteur} · {action?.ligne.token_used} Tips · <strong>{montantAction} F CFA</strong>
            {action?.ligne.phone_number ? ` · Wave ${action.ligne.phone_number}` : ''}
          </Typography>
          {action?.type === 'approuver' && (
            <Typography sx={{ fontSize: 13 }}>
              {action.ligne.status === 'premiere_approbation'
                ? 'Second avis : vous confirmez la décision d’un autre admin.'
                : 'Après approbation, le virement Wave est à faire puis à marquer versé avec sa référence.'}
            </Typography>
          )}
          {action?.type === 'rejeter' && (
            <TextField autoFocus fullWidth label="Motif du rejet (obligatoire, visible du Spotter)"
              value={saisie} onChange={e => setSaisie(e.target.value)} />
          )}
          {action?.type === 'wave' && (
            <Typography sx={{ fontSize: 13 }}>
              Le serveur envoie {montantAction} F CFA sur le compte Wave du Spotter
              {action.ligne.phone_number ? ` (${action.ligne.phone_number})` : ''}. L’argent part immédiatement :
              confirmez seulement si le montant et le numéro sont justes. En cas d’échec, le retrait reste à verser.
            </Typography>
          )}
          {action?.type === 'verser' && (
            <TextField autoFocus fullWidth label="Référence de la transaction Wave (obligatoire)"
              value={saisie} onChange={e => setSaisie(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={enCours}>Annuler</Button>
          <Button variant="contained" onClick={executer}
            disabled={enCours || (saisieRequise && saisie.trim() === '')}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* En-tête */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <TitrePage domaine="argent" aide={activeTab === 1 ? AIDE_ARGENT.retraits : AIDE_ARGENT.soldes}>{activeTab === 1 ? "Retraits" : "Soldes des Spotters"}</TitrePage>
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>
            {reglages
              ? `1 Tip = ${reglages.tipValueCfa} F CFA · minimum ${reglages.retraitMinimumCfa.toLocaleString('fr-FR')} F CFA par retrait · plafond ${reglages.retraitPlafondMensuelCfa.toLocaleString('fr-FR')} F CFA par mois et par Spotter`
              : 'Réglages d’argent indisponibles'}
          </Typography>
        </Box>
        {activeTab === 1 && (
          <Button variant="outlined" onClick={handleExportMensuel} sx={{
            textTransform: 'none', color: C.accentLight, borderColor: C.borderAccent, fontSize: 13, px: 2,
            '&:hover': { borderColor: C.accentLight, background: C.accentMuted },
          }}>
            Exporter la file
          </Button>
        )}
      </Box>

      {/* Onglets */}
      <Box sx={{ borderBottom: `1px solid ${C.border}`, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTabs-indicator':          { backgroundColor: C.accentLight },
            '& .MuiTab-root':                { textTransform: 'none', color: C.textMuted, fontWeight: 500, fontSize: 14 },
            '& .MuiTab-root.Mui-selected':   { color: C.accentLight, fontWeight: 600 },
          }}
        >
          <Tab label="Soldes des Spotters" />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Retraits
              {file.length > 0 && (
                <Chip label={file.length} size="small" sx={{
                  background: 'rgba(124,86,0,0.15)', color: C.warning,
                  border: `1px solid rgba(124,86,0,0.35)`, fontSize: 11, fontWeight: 700, height: 18,
                }} />
              )}
            </Box>
          } />
        </Tabs>
      </Box>

      {/* ── Onglet 0 ─────────────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {FILTER_LABELS.map(({ key, label }) => (
              <Box key={key} onClick={() => setFilter(key)} sx={{
                px: 2, py: 0.75, borderRadius: '7px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: `1px solid ${filter === key ? C.accentLight : 'rgba(31,30,27,0.08)'}`,
                background: filter === key ? C.accentMuted : 'transparent',
                color: filter === key ? C.accentLight : C.textMuted,
                transition: 'all 0.15s ease', userSelect: 'none',
              }}>
                {label}
              </Box>
            ))}
            <TextField
              placeholder="Rechercher..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              variant="outlined" size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.textMuted, fontSize: 18 }} /></InputAdornment> }}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': {
                  background: C.card, color: C.text, borderRadius: '7px',
                  '& fieldset': { borderColor: C.border },
                  '&:hover fieldset': { borderColor: C.borderAccent },
                  '&.Mui-focused fieldset': { borderColor: C.accentLight },
                },
              }}
            />
          </Box>

          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', 'Spotter', 'Tips disponibles', 'Tips cumulés', 'Solde F CFA', 'Progression', 'Statut'].map(label => (
                      <TableCell key={label} sx={{
                        background: C.card, color: C.accentLight, fontWeight: 700,
                        fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em',
                        borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                      }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slicedRows.map((c, index) => {
                    const pct = minimum && c.solde_cfa != null ? Math.min((c.solde_cfa / minimum) * 100, 100) : 0;
                    const pret = c.pret_au_retrait === true;
                    const couleur = pret ? C.success : pct >= 70 ? C.warning : C.accent;
                    return (
                      <TableRow key={c.user_id} sx={{
                        background: 'rgba(31,30,27,0.01)',
                        '&:hover': { background: 'rgba(31,30,27,0.04)' },
                        '& .MuiTableCell-root': { borderBottom: `1px solid ${C.border}`, py: 1.5 },
                      }}>
                        <TableCell>
                          <Typography sx={{ fontSize: 12, color: C.textMuted }}>{page * rowsPerPage + index + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <CollectorAvatar email={c.email || '?'} />
                            <Typography sx={{ fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {maskEmail(c.email || '')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.warning }}>{Number(c.token_avaliable || 0).toLocaleString('fr-FR')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, color: C.textSub }}>{Number(c.token_allocated || 0).toLocaleString('fr-FR')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                            {c.solde_cfa == null ? '—' : `${Number(c.solde_cfa).toLocaleString('fr-FR')} F CFA`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, height: 4, borderRadius: '3px', background: 'rgba(31,30,27,0.06)', overflow: 'hidden' }}>
                              <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: '3px', background: couleur, transition: 'width 0.3s ease' }} />
                            </Box>
                            <Typography sx={{ fontSize: 10, color: C.textMuted, minWidth: 32, textAlign: 'right' }}>{Math.round(pct)}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: pret ? C.success : C.textMuted }}>
                            {c.pret_au_retrait == null ? '—' : pret ? 'Prêt' : 'Sous le minimum'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {slicedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: C.textMuted, borderBottom: 'none' }}>
                        Aucun Spotter trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]} component="div"
              count={filteredRows.length} rowsPerPage={rowsPerPage} page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
              sx={{ color: C.textSub, borderTop: `1px solid ${C.border}` }}
            />
            <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 2, borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.success }}>{readyCount} prêt{readyCount !== 1 ? 's' : ''} au retrait</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.accentLight }}>Total : {totalTips.toLocaleString('fr-FR')} Tips disponibles</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* ── Onglet 1 ─────────────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <>
          <Box sx={{ background: 'rgba(124,86,0,0.08)', border: '1px solid rgba(124,86,0,0.25)', borderRadius: '8px', p: '12px 16px', mb: 3 }}>
            <Typography sx={{ fontSize: 13, color: C.warning }}>
              Montants calculés et règles appliquées par le serveur. Chaque décision est tracée (auteur, date) ; le rejet exige un motif, le versement une référence Wave.
              {reglages?.retraitDoubleApprobationActive
                ? ` Second avis d’un autre admin au-delà de ${reglages.retraitSeuilDoubleApprobationCfa.toLocaleString('fr-FR')} F CFA.`
                : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2, mb: 3 }}>
            {[
              { label: 'À décider',            value: enAttente.length,                                   color: C.warning },
              { label: 'Montant à décider',    value: `${montantFile.toLocaleString('fr-FR')} F CFA`,    color: C.accentLight },
              { label: 'Approuvés à verser',   value: aVerser.length,                                     color: C.success },
              { label: 'Montant à verser',     value: `${montantAVerser.toLocaleString('fr-FR')} F CFA`, color: C.accentLight },
            ].map(({ label, value, color }) => (
              <Box key={label} sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', p: '16px 20px' }}>
                <Typography sx={{ fontSize: 12, color: C.textMuted, textTransform: 'uppercase', fontWeight: 600, mb: 1 }}>{label}</Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, color }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <TextField
              placeholder="Rechercher..." variant="outlined" size="small" autoComplete="off"
              value={filterText} onChange={e => setFilterText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: C.card, color: C.text,
                  '& fieldset': { borderColor: C.border },
                  '&:hover fieldset': { borderColor: C.borderAccent },
                  '&.Mui-focused fieldset': { borderColor: C.accentLight },
                },
                '& .MuiInputBase-input::placeholder': { color: C.textMuted },
                minWidth: 240,
              }}
            />
          </Box>

          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', overflow: 'hidden' }}>
            <DataGrid
              rows={filteredFileRows} columns={columns}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
              pageSizeOptions={[10, 25, 50]}
              sx={{
                background: 'transparent', border: '1px solid rgba(31,30,27,0.06)', borderRadius: '8px', color: '#1F1E1B',
                '& .MuiDataGrid-columnHeaders':          { background: 'rgba(31,30,27,0.02)', borderBottom: '1px solid rgba(31,30,27,0.06)', color: '#57534A', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px' },
                '& .MuiDataGrid-row':                    { borderBottom: '1px solid rgba(31,30,27,0.04)', color: '#1F1E1B' },
                '& .MuiDataGrid-row:hover':              { background: 'rgba(31,30,27,0.02)' },
                '& .MuiDataGrid-cell':                   { borderBottom: 'none' },
                '& .MuiDataGrid-footerContainer':        { background: 'transparent', borderTop: '1px solid rgba(31,30,27,0.05)', color: '#57534A' },
                '& .MuiDataGrid-overlay':                { background: 'transparent', color: '#57534A' },
                '& .MuiTablePagination-root':            { color: '#57534A' },
                '& .MuiSvgIcon-root':                    { color: '#57534A' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none !important' },
                '& .MuiDataGrid-columnHeader:focus':     { outline: 'none !important' },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
